import { useState, useEffect, useMemo } from 'react';
import { X, Search } from 'lucide-react';
import '../styles/AttendanceModal.css';

export default function AttendanceModal({ sessionId, isOpen, onClose }) {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [studentQuery, setStudentQuery] = useState('');

    useEffect(() => {
        if (!isOpen || !sessionId) return;

            const fetchStudents = async () => {
                setLoading(true);
                try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/lecturer/attendance/session/${sessionId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const data = await response.json();
                
                    
                    const prepared = data.map(s => ({
                        ...s,
                        status: s.status === null ? 'not_checked' : s.status
                    }));
                    setStudents(prepared);
                } catch (err) {
                    console.error(err);
                } finally {
                    setLoading(false);
                }
            };

         fetchStudents();
    }, [sessionId, isOpen]);

    const updateStatus = async (studentId, newStatus) => {
        try {
            const token = localStorage.getItem('token');
            await fetch(`${process.env.REACT_APP_API_URL}/lecturer/attendance/session/${sessionId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ studentId, status: newStatus })
            });

            // Aktualizacja lokalnego stanu
            setStudents(prev => prev.map(s => s.student_id === studentId ? { ...s, status: newStatus } : s));
        } catch (err) {
            console.error(err);
        }
    };

    // Filtrowanie studentów na podstawie zapytania
    const filteredStudents = useMemo(() => {
        if (!studentQuery.trim()) return students;
        
        const query = studentQuery.trim().toLowerCase();
        return students.filter(student => {
            const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
            const studentNumber = String(student.student_number || '').toLowerCase();
            return fullName.includes(query) || studentNumber.includes(query);
        });
    }, [students, studentQuery]);

    // Resetuj zapytanie przy zamknięciu modala
    useEffect(() => {
        if (!isOpen) {
            setStudentQuery('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay-attendance">
            <div className="modal-content-attendance">
                <div className="modal-header">
                    <h2>Sprawdzanie obecności</h2>
                    <button onClick={onClose} className="close-btn">
                        <X size={20} />
                    </button>
                </div>
                
                {!loading && students.length > 0 && (
                    <div className="modal-search">
                        <div className="modal-search-input">
                            <Search size={18} />
                            <input
                                type="text"
                                className="modal-search-field"
                                value={studentQuery}
                                onChange={(e) => setStudentQuery(e.target.value)}
                                placeholder="Szukaj po imieniu, nazwisku lub numerze indeksu…"
                            />
                            {studentQuery && (
                                <button
                                    type="button"
                                    className="modal-search-clear"
                                    onClick={() => setStudentQuery('')}
                                    aria-label="Wyczyść wyszukiwanie"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                        {studentQuery && (
                            <div className="modal-search-results">
                                Znaleziono: {filteredStudents.length} z {students.length}
                            </div>
                        )}
                    </div>
                )}

                <div className="modal-body">
                    {loading ? (
                        <p>Ładowanie...</p>
                    ) : students.length === 0 ? (
                        <p>Brak studentów w tej sesji.</p>
                    ) : filteredStudents.length === 0 ? (
                        <p>Nie znaleziono studentów pasujących do wyszukiwania.</p>
                    ) : (
                        <table className="modal-table">
                            <thead>
                                <tr>
                                    <th>Numer</th>
                                    <th>Imię i nazwisko</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStudents.map(student => (
                                    <tr key={student.student_id}>
                                        <td>{student.student_number}</td>
                                        <td>{student.first_name} {student.last_name}</td>
                                        <td>
                                            <select
                                                value={student.status}
                                                onChange={e => updateStatus(student.student_id, e.target.value)}
                                            >
                                                <option value="not_checked">Nie sprawdzono</option>
                                                <option value="present">Obecny</option>
                                                <option value="late">Spóźniony</option>
                                                <option value="absent">Nieobecny</option>
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
